import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const LEGAL_LINKS = [
  ['Privacy', 'https://coffeechats.me/privacy-policy'],
  ['Terms', 'https://coffeechats.me/terms-of-service'],
];

const SOCIAL_LINKS = [
  ['Instagram', 'https://www.instagram.com/coffeechatsme'],
  ['LinkedIn', 'https://www.linkedin.com/company/coffee-chats-ai/'],
  ['X', 'https://x.com/coffeechatsme'],
];

function SocialMark({ name }) {
  if (name === 'Instagram') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" className="fill-current stroke-none" />
      </svg>
    );
  }
  if (name === 'LinkedIn') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-current">
        <path d="M5.3 7.7A2.2 2.2 0 1 0 5.3 3.3a2.2 2.2 0 0 0 0 4.4ZM3.4 20.7h3.8V9.2H3.4v11.5ZM9.4 9.2h3.6v1.6h.1c.5-.9 1.7-2 3.6-2 3.8 0 4.5 2.5 4.5 5.8v6.1h-3.8v-5.4c0-1.3 0-3-1.9-3s-2.2 1.4-2.2 2.9v5.5H9.4V9.2Z" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-current">
      <path d="M18.9 3H22l-6.8 7.8L23.2 21H17l-4.9-6.4L6.5 21H3.4l7.2-8.3L2.9 3h6.3l4.4 5.8L18.9 3Zm-1.1 16h1.7L8.3 4.9H6.5L17.8 19Z" />
    </svg>
  );
}

function SupportMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.7 9a2.5 2.5 0 1 1 4.5 1.5c-.9 1-2.2 1.3-2.2 2.8" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function ShieldMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5.5c0 4.3-2.9 8.1-7 9.5-4.1-1.4-7-5.2-7-9.5V6l7-3Z" />
      <path d="M9.5 12.2l1.8 1.8 3.4-3.4" />
    </svg>
  );
}

function LogOutMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 17l5-5-5-5M15 12H3" />
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
    </svg>
  );
}

export default function AccountMenu({
  profile,
  onSignOut,
  onSupport,
  // Optional. Both administrative bundles pass it; non-admin consumers can
  // omit it and render no security action.
  onTwoFactor,
  busy = false,
  showSignOut = true,
  renderTrigger,
  triggerClassName = '',
  triggerStyle,
  interactionBackgroundColor,
  menuClassName = 'left-0 bottom-full mb-3 w-full min-w-[220px]',
  // Escape an ancestor's overflow clip by rendering the panel into <body> and
  // positioning it from the trigger's rect. The collapsed console rail sets
  // overflow-x-hidden for its width animation, which silently ate this menu:
  // it mounted, it was just invisible.
  portalToBody = false,
  t,
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const fullName = profile?.name?.trim() || profile?.email?.split('@')[0] || 'Administrator';

  useEffect(() => {
    if (!open) { return undefined; }
    const closeOnOutsidePress = (event) => {
      const inside = menuRef.current?.contains(event.target)
        || panelRef.current?.contains(event.target);
      if (!inside) { setOpen(false); }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  // Re-measured on scroll/resize because a fixed panel does not travel with
  // its anchor. Capture-phase scroll so it also follows an inner scroller.
  useEffect(() => {
    if (!open || !portalToBody) { return undefined; }
    const measure = () => { setAnchor(triggerRef.current?.getBoundingClientRect() || null); };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, portalToBody]);

  const renderPanel = (node) => {
    // Portal only once the anchor is measured, so the panel never paints at 0,0
    // for a frame before jumping into place.
    if (!portalToBody) { return node; }
    if (!anchor) { return null; }
    return createPortal(node, document.body);
  };

  const interactionBg = interactionBackgroundColor || t.pillActiveBg;
  const hoverOn = (event) => { event.currentTarget.style.backgroundColor = interactionBg; };
  const hoverOff = (event) => { event.currentTarget.style.backgroundColor = 'transparent'; };

  return (
    <div ref={menuRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen((value) => { return !value; }); }}
        className={`w-full rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${triggerClassName}`}
        style={{ ...triggerStyle, backgroundColor: open ? interactionBg : 'transparent' }}
      >
        {renderTrigger({ open })}
      </button>

      {open ? renderPanel(
        <div
          ref={panelRef}
          role="menu"
          aria-label="Account menu"
          className={portalToBody
            ? 'fixed z-[100] w-72 overflow-hidden rounded-xl border shadow-xl'
            : `absolute z-50 overflow-hidden rounded-xl border shadow-xl ${menuClassName}`}
          style={portalToBody
            ? {
              backgroundColor: t.surface,
              borderColor: t.border,
              // Sits to the RIGHT of the collapsed rail, bottom-aligned with the
              // trigger, mirroring the non-portaled `left-full bottom-0 ml-3`.
              left: (anchor?.right ?? 0) + 12,
              top: Math.max(8, (anchor?.bottom ?? 0) - 320),
            }
            : { backgroundColor: t.surface, borderColor: t.border }}
        >
          <div className="px-4 py-3 min-w-0">
            <div className="truncate text-[14px] font-semibold" style={{ color: t.heading, fontFamily: t.fonts.body }}>
              {fullName}
            </div>
            {profile?.email ? (
              <div className="mt-1 truncate text-[12px]" style={{ color: t.muted, fontFamily: t.fonts.body }}>
                {profile.email}
              </div>
            ) : null}
          </div>

          {onTwoFactor ? (
            <div className="border-t p-1.5" style={{ borderColor: t.hairline }}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onTwoFactor();
                }}
                className={`flex min-h-[40px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[14px] font-medium transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                style={{ color: t.heading, fontFamily: t.fonts.body }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span style={{ color: t.muted }}><ShieldMark /></span>
                Two-factor authentication
              </button>
            </div>
          ) : null}

          {onSupport ? (
            <div className="border-t p-1.5" style={{ borderColor: t.hairline }}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onSupport();
                }}
                className={`flex min-h-[40px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[14px] font-medium transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                style={{ color: t.heading, fontFamily: t.fonts.body }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span style={{ color: t.muted }}><SupportMark /></span>
                Support
              </button>
            </div>
          ) : null}

          {showSignOut ? (
            <div className="border-t p-1.5" style={{ borderColor: t.hairline }}>
              <button
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  onSignOut?.();
                }}
                className={`flex min-h-[40px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[14px] font-medium transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing} ${busy ? 'cursor-not-allowed opacity-55' : ''}`}
                style={{ color: t.danger, fontFamily: t.fonts.body }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <LogOutMark />
                {busy ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3 border-t px-3 py-2.5" style={{ borderColor: t.hairline }}>
            <div className="flex items-center gap-3">
              {LEGAL_LINKS.map(([label, href]) => {
                return (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    role="menuitem"
                    onClick={() => { setOpen(false); }}
                    className={`rounded text-[11px] font-medium focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                    style={{ color: t.muted, fontFamily: t.fonts.body }}
                  >
                    {label}
                  </a>
                );
              })}
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {SOCIAL_LINKS.map(([label, href]) => {
                return (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    role="menuitem"
                    aria-label={label}
                    title={label}
                    onClick={() => { setOpen(false); }}
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                    style={{ color: t.muted }}
                    onMouseEnter={hoverOn}
                    onMouseLeave={hoverOff}
                  >
                    <SocialMark name={label} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>,
      ) : null}
    </div>
  );
}
